// Connection state for the browser surface.
//
// This was the Electron offline controller: work modes, local-DB sync,
// periodic downloads, a heartbeat, and a nightly-sync IPC subscription. All of
// that went with Electron. What remains is the one thing the browser genuinely
// needs and never had — an honest answer to "is the backend reachable?".
//
// It previously hard-coded `isOnline: true` for every non-Electron host, so the
// browser POS could not tell it had lost connection, and a Cloud Run cold start
// looked identical to a healthy backend.
//
// The mechanics are shared with the native shell's ConnectionProvider via
// connection/probe.js, so the two surfaces cannot drift apart.
import React, { createContext, useContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import api from '../services/api';
import {
    connectionReducer, initialConnectionState, pingHealth, DEFAULT_PING_INTERVAL_MS,
} from '../connection/probe';

const SyncContext = createContext(null);

export const SyncProvider = ({ children, pingIntervalMs = DEFAULT_PING_INTERVAL_MS }) => {
    const [state, dispatch] = useReducer(connectionReducer, undefined, initialConnectionState);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const probe = useCallback(async () => {
        const ok = await pingHealth(api);
        if (!mountedRef.current) return;
        dispatch({ type: ok ? 'PING_SUCCESS' : 'PING_FAILURE' });
    }, []);

    useEffect(() => {
        const onOnline = () => { dispatch({ type: 'NAVIGATOR_ONLINE' }); probe(); };
        const onOffline = () => dispatch({ type: 'NAVIGATOR_OFFLINE' });
        const onFocus = () => probe();

        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        window.addEventListener('focus', onFocus);

        const id = setInterval(() => {
            // Skip while the tab is hidden: a backgrounded tab polling every
            // 30s is noise, and focus re-checks the moment it matters.
            if (typeof document !== 'undefined' && document.hidden) return;
            probe();
        }, pingIntervalMs);

        probe();

        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('focus', onFocus);
            clearInterval(id);
        };
    }, [probe, pingIntervalMs]);

    const value = useMemo(
        () => ({ isOnline: state.online, reconnecting: state.reconnecting }),
        [state.online, state.reconnecting],
    );

    return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

export const useSync = () => {
    const ctx = useContext(SyncContext);
    return ctx || { isOnline: true, reconnecting: false };
};

export default SyncContext;
